import React from 'react';
import * as S from './components/LogTable.styled';
import UsernamePhotoWithPreview from './components/UsernamePhotoWithPreview';
import Room from '@mui/icons-material/MeetingRoom';
import Branches from '@mui/icons-material/AccountTree';
import Flight from '@mui/icons-material/Flight';
import Hotel from '@mui/icons-material/Hotel';
import Home from '@mui/icons-material/Home';
import Apartment from '@mui/icons-material/Apartment';
import Business from '@mui/icons-material/Business';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Person from '@mui/icons-material/Person';
import { parseLogMessageSegments, parseLogPhotoIds } from './logFormat';
import {
  REMOVED_ACTOR_LABEL,
  isPersistedActorStaffCodeBold,
  resolveActorDisplayName,
  resolveActorStaffCode,
  resolveActorUser,
  StaffMaps,
} from './logActor';
import {
  CustomerMaps,
  REMOVED_CUSTOMER_LABEL,
  resolveCustomer,
  resolveCustomerDisplayName,
} from './logCustomer';
import type { Log } from './hooks';
import { isLogDeletedType } from './activityLogLabels';
import { buildPhotosTableSrc } from './photosTableSrc';

/** Channel-manager OTA keys → display label + brand-tinted chip + icon. */
const otaMeta: Record<string, {
  label: string;
  accent: string;
  darkText?: boolean;
  Icon: typeof Flight;
}> = {
  BookingCom: { label: 'Booking.com', accent: '#003580', Icon: Hotel },
  Expedia: { label: 'Expedia', accent: '#FFC72C', darkText: true, Icon: Flight },
  Airbnb: { label: 'Airbnb', accent: '#FF5A5F', Icon: Home },
  Agoda: { label: 'Agoda', accent: '#E21E26', Icon: Apartment },
  HotelsCom: { label: 'Hotels.com', accent: '#D32F2F', Icon: Business },
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type FormatActivityMessageOptions = {
  currency: string;
  staffMaps: StaffMaps;
  customerMaps: CustomerMaps;
  /** Staff id/username → employeeNumber for Name(CODE) on actor chips. */
  staffCodeByToken?: Map<string, string>;
  /** erp_records id → Name(CODE) for legacy change lines that stored raw UUIDs. */
  erpLabelById?: Map<string, string>;
  userPhotoByName?: Map<string, string>;
  customerPhotoByToken?: Map<string, string>;
  /** Value column: show unit counts instead of money. */
  unitsMode?: boolean;
  /** Parent row — used for deleted-activity preview on &L& links. */
  parentLog?: Log;
  canPreviewDeletedActivity?: boolean;
  /** Organization id for photos-table URLs (work-order form photos). */
  organizationId?: string | number | null;
  // eslint-disable-next-line no-unused-vars
  onActivityIdClick?: (targetId: string, kind: 'activity' | 'restored') => void;
  // eslint-disable-next-line no-unused-vars
  onPreviewDeletedActivity?: (targetId: string) => void;
  /** Open a form photo large in the RightBar overlay. */
  // eslint-disable-next-line no-unused-vars
  onLogPhotoClick?: (src: string, label: string) => void;
};

/**
 * Same message/value token rendering as /logs details.
 * Shared by LogTable and Reporting preview.
 */
export const formatActivityMessageEntry = (
  entry: string,
  opts: FormatActivityMessageOptions,
): React.ReactNode[] => {
  // Seeded / sparse rows can omit message — avoid .match on undefined.
  if (entry == null || entry === '') {
    return [];
  }
  const {
    currency,
    staffMaps,
    customerMaps,
    staffCodeByToken,
    erpLabelById = new Map(),
    userPhotoByName = new Map(),
    customerPhotoByToken = new Map(),
    unitsMode,
    parentLog,
    canPreviewDeletedActivity,
    organizationId,
    onActivityIdClick,
    onPreviewDeletedActivity,
    onLogPhotoClick,
  } = opts;

  const entryText = String(entry);
  const numberEntry = Number(entryText);
  if (numberEntry || numberEntry === 0 || entryText === 'N/A') {
    if (entryText === 'N/A') {
      return [<span key="na">N/A</span>];
    }
    if (unitsMode) {
      return [
        <S.UnitsEntry key="units">{numberEntry.toLocaleString()}</S.UnitsEntry>,
      ];
    }
    if (numberEntry === 0) {
      return [<span key="zero">0</span>];
    }
    if (numberEntry > 0) {
      return [
        <S.CashIn key="cashin">{`${currency}${numberEntry.toLocaleString()}`}</S.CashIn>,
      ];
    }
    return [
      <S.CashOut key="cashout">{`${currency}${numberEntry.toLocaleString()}`}</S.CashOut>,
    ];
  }

  const renderActor = (text: string, staffCode: string) => {
    const user = resolveActorUser(text, staffMaps);
    const isRemoved = !user?.username;
    const displayName = isRemoved
      ? REMOVED_ACTOR_LABEL
      : resolveActorDisplayName(text, staffMaps, text);
    const photoSrc = isRemoved
      ? undefined
      : (userPhotoByName.get(String(text))
        || userPhotoByName.get(displayName.replace(/\s+/g, '').toLowerCase()));
    return (
      <S.Username isRemoved={isRemoved}>
        <UsernamePhotoWithPreview
          src={photoSrc}
          fallback={(
            <S.UsernamePhotoFallback aria-hidden isRemoved={isRemoved}>
              <Person />
            </S.UsernamePhotoFallback>
          )}
        />
        <S.UsernameText isRemoved={isRemoved}>
          {displayName}
          {!isRemoved && staffCode ? <S.Bold>{`(${staffCode})`}</S.Bold> : null}
        </S.UsernameText>
      </S.Username>
    );
  };

  const renderCustomer = (text: string) => {
    const customer = resolveCustomer(text, customerMaps);
    const isRemoved = !customer?.name;
    const displayName = isRemoved
      ? REMOVED_CUSTOMER_LABEL
      : resolveCustomerDisplayName(text, customerMaps, text);
    const photoSrc = isRemoved
      ? undefined
      : (customerPhotoByToken.get(String(text))
        || customerPhotoByToken.get(displayName.replace(/\s+/g, '').toLowerCase()));
    return (
      <S.Customer isRemoved={isRemoved}>
        <UsernamePhotoWithPreview
          src={photoSrc}
          ring
          fallback={(
            <S.UsernamePhotoFallback aria-hidden isRemoved={isRemoved} $ring>
              <Person />
            </S.UsernamePhotoFallback>
          )}
        />
        <S.UsernameText isRemoved={isRemoved}>{displayName}</S.UsernameText>
      </S.Customer>
    );
  };

  const renderKind = (kind: string, text: string): React.ReactNode => {
    if (kind === 'desc' || kind === 'bold') {
      // Compliance standing terms → colored badge (also remaps legacy low-level *).
      const complianceLabel = S.complianceStandingDisplayLabel(text);
      if (complianceLabel) {
        return (
          <S.StatusBadge $tone={S.statusBadgeToneForText(complianceLabel)}>
            {complianceLabel}
          </S.StatusBadge>
        );
      }
      // Legacy record-change lines stored bare erp ids — show Name(CODE) when known.
      const resolvedErp = UUID_RE.test(text) ? erpLabelById.get(text) : '';
      return <S.Bold>{resolvedErp || text}</S.Bold>;
    }
    if (kind === 'customer') return renderCustomer(text);
    if (kind === 'room') {
      return (
        <S.Room>
          <Room fontSize="small" sx={{ scale: '0.65' }} />
          <S.RoomText>{text}</S.RoomText>
        </S.Room>
      );
    }
    if (kind === 'dept') {
      return (
        <S.External>
          <Branches fontSize="small" sx={{ scale: '0.65' }} />
          <S.ExternalText>{text}</S.ExternalText>
        </S.External>
      );
    }
    if (kind === 'status') {
      const complianceLabel = S.complianceStandingDisplayLabel(text);
      const label = complianceLabel || text;
      const tone = S.statusBadgeToneForText(label);
      return (
        <S.StatusBadge $tone={tone}>
          {label}
        </S.StatusBadge>
      );
    }
    if (kind === 'price') {
      const amount = Number(String(text).replace(/,/g, ''))
      const safe = Number.isFinite(amount) ? amount : 0
      return (
        <S.PriceAmount $negative={safe < 0}>
          {`${currency}${safe.toLocaleString()}`}
        </S.PriceAmount>
      );
    }
    if (kind === 'ota') {
      const resolved = otaMeta[text];
      const Icon = resolved?.Icon || Flight;
      return (
        <S.OTA
          $accent={resolved?.accent}
          $darkText={Boolean(resolved?.darkText)}
        >
          <Icon fontSize="small" sx={{ scale: '0.6' }} />
          {resolved?.label || text}
        </S.OTA>
      );
    }
    if (kind === 'arrow') {
      return (
        <S.StockArrow aria-hidden>
          <ArrowForward fontSize="small" sx={{ fontSize: 14 }} />
        </S.StockArrow>
      );
    }
    if (kind === 'activity') {
      const targetId = String(text || '').trim();
      const canPreview = Boolean(
        parentLog
        && canPreviewDeletedActivity
        && isLogDeletedType(parentLog.type)
        && parentLog.logTable?.id,
      );
      return (
        <S.ActivityIdLink
          type="button"
          title={canPreview ? 'Preview deleted log' : 'Go to log'}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!targetId) return;
            if (canPreview && onPreviewDeletedActivity) {
              onPreviewDeletedActivity(targetId);
              return;
            }
            onActivityIdClick?.(targetId, 'activity');
          }}
        >
          log
        </S.ActivityIdLink>
      );
    }
    if (kind === 'restored') {
      const targetId = String(text || '').trim();
      return (
        <S.RestoredIdLink
          type="button"
          title="Go to restore log"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!targetId) return;
            onActivityIdClick?.(targetId, 'restored');
          }}
        >
          restored
        </S.RestoredIdLink>
      );
    }
    if (kind === 'booking') return <></>;
    if (kind === 'photo') {
      const ids = parseLogPhotoIds(text);
      if (!ids.length) return <S.LogPhotoMissing>photo</S.LogPhotoMissing>;
      return (
        <S.LogPhotoScroll role="list" aria-label={ids.length === 1 ? 'Photo' : `${ids.length} photos`}>
          {ids.map((photoId, photoIndex) => {
            const src = buildPhotosTableSrc(organizationId, photoId);
            const label = ids.length === 1 ? 'Photo' : `Photo ${photoIndex + 1}`;
            return (
              <S.LogPhotoChip
                key={`${photoId}_${photoIndex}`}
                type="button"
                role="listitem"
                title={label}
                disabled={!src || !onLogPhotoClick}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!src || !onLogPhotoClick) return;
                  onLogPhotoClick(src, label);
                }}
              >
                {src ? (
                  <S.LogPhotoThumb src={src} alt="" />
                ) : (
                  <S.LogPhotoMissing aria-hidden>…</S.LogPhotoMissing>
                )}
                <S.LogPhotoChipLabel>{label}</S.LogPhotoChipLabel>
              </S.LogPhotoChip>
            );
          })}
        </S.LogPhotoScroll>
      );
    }
    return text;
  };

  const renderPlain = (text: string, keyBase: string): React.ReactNode[] => {
    if (!text) return [];
    if (!text.includes('%')) return [text];
    const bits = text.split('%');
    const nodes: React.ReactNode[] = [];
    bits.forEach((bit, i) => {
      if (bit) nodes.push(bit);
      if (i < bits.length - 1) nodes.push(<div key={`${keyBase}-br-${i}`} />);
    });
    return nodes;
  };

  const nodes: React.ReactNode[] = [];
  const segments = parseLogMessageSegments(entryText);
  segments.forEach((seg, i) => {
    if (seg.type === 'text') {
      nodes.push(...renderPlain(seg.text, `t${i}`));
      return;
    }
    if (seg.kind === 'actor') {
      const staffCode = resolveActorStaffCode(seg.inner, staffMaps, staffCodeByToken);
      nodes.push(
        <React.Fragment key={`w${i}`}>
          {renderActor(seg.inner, staffCode)}
        </React.Fragment>,
      );
      return;
    }
    const prev = segments[i - 1];
    if (
      seg.kind === 'bold'
      && prev
      && prev.type !== 'text'
      && prev.kind === 'actor'
    ) {
      const staffCode = resolveActorStaffCode(prev.inner, staffMaps, staffCodeByToken);
      if (isPersistedActorStaffCodeBold(seg.inner, staffCode)) return;
    }
    nodes.push(
      <React.Fragment key={`w${i}`}>
        {renderKind(seg.kind, seg.inner)}
      </React.Fragment>,
    );
  });
  return nodes;
};
